/**
 * Patient Requirements — the words the bulk launch surface uses (GDN-3230,
 * AC-10 … AC-14).
 *
 * Separate from `vocabulary.ts` because that file owns how ONE requirement on
 * ONE patient reads. This owns how a batch reads, and the two vocabularies get
 * one word dangerously wrong if they share a file: see BATCH_STATES below.
 */

/**
 * Why a patient in the missing population gets no message.
 *
 * The closed set the backend derives (GDN-3228). `channel_suppressed` and
 * `channel_avoided` look alike and are not: suppressed is AC-22's carrier
 * opt-out, a compliance fact staff cannot clear from the preferences UI;
 * avoided is a staff-editable preference. Staff who read them as the same
 * thing will go and "fix" the one they must not touch.
 *
 * A patient who already meets the requirement is NOT excluded — they land in
 * the already-complete bucket instead. That is what makes eligible +
 * already_complete + excluded add up to the population the preview read: one
 * patient, one primary reason, and the three headline numbers sum.
 */
export const EXCLUSION_REASONS = [
  'patient_inactive',
  'no_contact_info',
  'channel_suppressed',
  'channel_avoided',
  'active_session_exists',
  'patient_not_readable',
] as const;

export type ExclusionReason = (typeof EXCLUSION_REASONS)[number];

interface ExclusionCopy {
  /** Short label for a count line. */
  label: string;
  /** One sentence of what staff can do about it, or why they cannot. */
  detail: string;
  /** True when staff must NOT try to undo it. Drives the tone, not just the words. */
  immutable: boolean;
}

const EXCLUSION_COPY: Record<ExclusionReason, ExclusionCopy> = {
  patient_inactive: {
    label: 'Inactive',
    detail: 'Excluded from this batch because their record is inactive. Reactivate their record to include them in future batches.',
    immutable: false,
  },
  no_contact_info: {
    label: 'No phone or email on file',
    detail: 'Add a mobile number or an email address to their record and they will be included next time.',
    immutable: false,
  },
  channel_suppressed: {
    label: 'Opted out with the carrier',
    detail:
      'They replied STOP to a text. This is their instruction to the carrier, not a setting — it cannot be changed here, and we do not send to them.',
    immutable: true,
  },
  channel_avoided: {
    label: 'Asked not to be contacted this way',
    detail: 'A communication preference on their record. Change it there if they have told you otherwise.',
    immutable: false,
  },
  active_session_exists: {
    label: 'Already has an open request',
    detail: 'They were asked recently and have not answered yet. Sending again would duplicate it.',
    immutable: false,
  },
  /**
   * An integrity bucket, not a decision about the person (#4408). It normally
   * reads 0; a persistent non-zero is a support case rather than something the
   * screen can explain, so it is styled like every other reason and not as an
   * error.
   *
   * The PM's copy reads "Patient record could not be read"; this says "Their
   * record" for the same meaning without the one word the repo forbids
   * hardcoding — a client-language practice would otherwise read "Patient"
   * here and its own word everywhere else on the panel.
   */
  patient_not_readable: {
    label: 'Their record could not be read',
    detail: 'Left out to be safe. Run the preview again.',
    immutable: false,
  },
};

const isExclusionReason = (value: string): value is ExclusionReason =>
  (EXCLUSION_REASONS as readonly string[]).includes(value);

/**
 * Staff-facing copy for an exclusion reason.
 *
 * An unrecognised reason keeps its raw value rather than collapsing into
 * "Other": a reason the backend added and the frontend has not learned yet is
 * still a real reason, and hiding it behind a generic word makes the excluded
 * count stop adding up on screen.
 */
export const exclusionCopy = (reason: string): ExclusionCopy =>
  isExclusionReason(reason)
    ? EXCLUSION_COPY[reason]
    : { label: reason, detail: '', immutable: false };

/**
 * The batch's own lifecycle (GDN-3228). Labelled to stay out of the way of the
 * OTHER completed on this screen.
 *
 * The endpoint's `completed` state means "we finished sending". The panel also
 * shows completed ITEMS, meaning patients who actually did the thing we asked.
 * Rendering both as "Completed" puts two different facts under one word in one
 * card — so the state reads "Finished sending", which is what it means.
 */
export const BATCH_STATES = ['queued', 'running', 'completed', 'cancelled'] as const;

export type BatchState = (typeof BATCH_STATES)[number];

const BATCH_STATE_LABELS: Record<BatchState, string> = {
  queued: 'Queued',
  running: 'Sending',
  completed: 'Finished sending',
  cancelled: 'Cancelled',
};

export const batchStateLabel = (state: string): string =>
  (BATCH_STATE_LABELS as Record<string, string>)[state] ?? state;

/**
 * The already-complete stat's label, in the requirement's own noun (PM ruling
 * on GDN-3228, 2026-09-04).
 *
 * This number is routinely non-zero on a list of patients who are supposedly
 * missing the requirement, because the preview verifies each row against the
 * source of truth as it reads. That looks like a contradiction, so it is
 * labelled and explained as an ordinary fact — never as a warning, and never
 * hidden behind a disclosure.
 */
const ALREADY_COMPLETE_LABELS: Record<string, string> = {
  payment_method: 'Already have a card',
  insurance_card: 'Already have an insurance card',
  form: 'Already completed it',
};

export const alreadyCompleteLabel = (kind?: string): string =>
  (kind ? ALREADY_COMPLETE_LABELS[kind] : undefined) ?? 'Already have it';

export const ALREADY_COMPLETE_DETAIL =
  'Completed since this list was made — checked just now, so they get no message.';

/** A batch that is still doing something is the only one worth polling. */
export const isBatchRunning = (state: string): boolean =>
  state === 'queued' || state === 'running';

/**
 * Patients and messages are different counts, and the launch surface shows both.
 *
 * The preview counts PATIENTS (`eligible_count`); progress counts MESSAGES,
 * because a patient reachable by both SMS and email is one eligible patient and
 * two rows of progress. 128 previewed then 217 sent is correct and reads like a
 * bug, so every number on this surface says which unit it is in.
 */
export const patientCount = (count: number, subjectPlural: string, subjectSingular: string): string =>
  `${count.toLocaleString()} ${count === 1 ? subjectSingular : subjectPlural}`;

export const messageCount = (count: number): string =>
  `${count.toLocaleString()} ${count === 1 ? 'message' : 'messages'}`;
